import * as React from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import { ComplaintMessageFiles } from 'modules/complaints/store/collections/complaintMessageFiles'
import { ComplaintMessages } from 'modules/complaints/store/collections/complaintMessages'
import complaints from 'modules/complaints/store/collections/complaints'
import type Complaint from 'modules/complaints/store/models/Complaint'

interface Props {
  complaintId: number | null
}

interface Values {
  loading: boolean
  fetch: () => Promise<void>
  complaint: Complaint | undefined
  complaintMessages: ComplaintMessages
  complaintMessageFiles: ComplaintMessageFiles
}

const complaintContext = React.createContext<Values | null>(null)

export const ComplaintProvider = ({ children, complaintId }: React.PropsWithChildren<Props>) => {
  const complaint = complaints.get(complaintId)
  const complaintMessages = React.useRef(new ComplaintMessages()).current

  const complaintMessageFiles = React.useRef(new ComplaintMessageFiles()).current

  const fetch = async () => {
    if (!complaintId) return

    const data = {
      complaint_id: complaintId,
    }

    const collections = [complaintMessages, complaintMessageFiles]

    await Promise.all(
      collections.map((c) =>
        c.fetch({
          data,
        })
      )
    )
  }

  const { loading } = useAsyncData(() => {
    return fetch()
  }, [complaintId])

  const values: Values = {
    loading,
    fetch,
    complaint,
    complaintMessages,
    complaintMessageFiles,
  }

  return <complaintContext.Provider value={values}>{children}</complaintContext.Provider>
}

export const useComplaint = () => {
  const value = React.useContext(complaintContext)

  if (!value) {
    throw new Error('Complain context not initialized')
  }

  return value
}
